import { forwardRef, type ButtonHTMLAttributes, type PropsWithChildren } from 'react'
import { cn } from '@/shared/lib/cn'
import { focusRingClasses } from '@/shared/lib/focusRing'
import { Tooltip } from '@/shared/ui/Tooltip'

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive'
type ButtonSize = 'default' | 'icon'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  /** default: altura mínima 44pt (R14). icon: quadrado 44×44pt para botões-ícone. */
  size?: ButtonSize
  fullWidth?: boolean
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-[var(--color-mint-strong)] text-white hover:opacity-90',
  secondary:
    'bg-[var(--color-mint-soft)] text-[var(--color-text-primary)] hover:bg-[var(--color-mint-soft)]/80',
  ghost:
    'bg-[var(--color-bg-surface)] text-[var(--color-text-secondary)] border border-[var(--color-border)] hover:bg-[var(--color-mint-soft)]',
  destructive:
    'bg-[var(--color-wine-soft)] text-[var(--color-wine-strong)] border border-[var(--color-wine-soft-border)] hover:bg-[var(--color-wine-soft-hover)]',
}

/** Classes reutilizáveis para botões-ícone ≥44pt. Espaçamento entre alvos adjacentes: gap-2 (≥8px). */
export const iconButtonTouchClasses =
  'inline-flex h-11 w-11 min-h-11 min-w-11 shrink-0 touch-manipulation items-center justify-center rounded-lg'

const sizeClasses: Record<ButtonSize, string> = {
  default: 'min-h-11 px-4 touch-manipulation',
  icon: iconButtonTouchClasses + ' p-0',
}

export const Button = forwardRef<HTMLButtonElement, PropsWithChildren<ButtonProps>>(
  (
    {
      children,
      className,
      variant = 'primary',
      size = 'default',
      fullWidth = false,
      type = 'button',
      title,
      ...props
    },
    ref,
  ) => {
    const button = (
      <button
        ref={ref}
        type={type}
        className={cn(
          'rounded-lg text-sm font-semibold transition-all duration-200 motion-reduce:transition-none active:scale-[0.99] motion-reduce:active:scale-100 disabled:cursor-not-allowed disabled:opacity-60',
          focusRingClasses,
          variantClasses[variant],
          sizeClasses[size],
          fullWidth && 'w-full',
          className,
        )}
        {...props}
      >
        {children}
      </button>
    )
    if (!title) return button
    return <Tooltip label={title}>{button}</Tooltip>
  },
)

Button.displayName = 'Button'
